"use client";

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertCircle, CheckCircle, XCircle, Landmark, ExternalLink, RefreshCw } from 'lucide-react';
import { useTraditionalAchApprovals, useApproveTraditionalAch, useRejectTraditionalAch } from '@/hooks/useTraditionalAchApprovals';
import { Skeleton } from '@/components/ui/skeleton';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

export default function TraditionalAchApprovalsPage() {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selectedApproval, setSelectedApproval] = useState<any>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');

  const { data: approvals, isLoading, refetch } = useTraditionalAchApprovals(activeTab);
  const approveMutation = useApproveTraditionalAch();
  const rejectMutation = useRejectTraditionalAch();

  const handleAction = async () => {
    if (!selectedApproval || !actionType) return;

    if (actionType === 'approve') {
      await approveMutation.mutateAsync({ approvalId: selectedApproval.id, adminNotes: notes });
    } else {
      await rejectMutation.mutateAsync({ approvalId: selectedApproval.id, rejectionReason: notes });
    }

    setSelectedApproval(null);
    setActionType(null);
    setNotes('');
  };

  const openDialog = (approval: any, type: 'approve' | 'reject') => {
    setSelectedApproval(approval);
    setActionType(type);
    setNotes('');
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="h-6 w-6" />
            Traditional ACH Approvals
          </h1>
          <p className="text-muted-foreground">
            Review and confirm Traditional ACH payments received outside Stripe
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : !approvals?.length ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No {activeTab} Traditional ACH payments
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {approvals.map((approval) => (
                <Card key={approval.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Landmark className="h-5 w-5 text-blue-600" />
                        {approval.reference_id}
                      </CardTitle>
                      <Badge variant={
                        approval.status === 'pending' ? 'default' :
                        approval.status === 'approved' ? 'secondary' : 'destructive'
                      }>
                        {approval.status === 'pending' && <AlertCircle className="h-3 w-3 mr-1" />}
                        {approval.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                        {approval.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                        {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                      </Badge>
                    </div>
                    <CardDescription>
                      {approval.jobs?.title || 'Job'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div>
                        <div className="text-muted-foreground">Client</div>
                        <div className="font-medium">{approval.organizations?.name || 'Unknown'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Amount</div>
                        <div className="font-medium text-lg">${(approval.amount_cents / 100).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Submitted</div>
                        <div className="font-medium">{format(new Date(approval.created_at), 'MMM d, yyyy')}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">
                          {approval.status === 'pending' ? 'Pending For' : 
                           approval.status === 'approved' ? 'Approved' : 'Rejected'}
                        </div>
                        <div className="font-medium">
                          {approval.status === 'pending' 
                            ? formatDistanceToNow(new Date(approval.created_at))
                            : format(new Date(approval.approved_at || approval.rejected_at || approval.updated_at), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>

                    {approval.admin_notes && (
                      <div className="bg-muted p-3 rounded-md mb-4">
                        <div className="text-xs text-muted-foreground mb-1">Admin Notes</div>
                        <div className="text-sm">{approval.admin_notes}</div>
                      </div>
                    )}

                    {approval.rejection_reason && (
                      <div className="bg-destructive/10 p-3 rounded-md mb-4">
                        <div className="text-xs text-destructive mb-1">Rejection Reason</div>
                        <div className="text-sm">{approval.rejection_reason}</div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      {approval.status === 'pending' && (
                        <>
                          <Button onClick={() => openDialog(approval, 'approve')} className="flex-1">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark as Received
                          </Button>
                          <Button variant="destructive" onClick={() => openDialog(approval, 'reject')}>
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </>
                      )}
                      <Button variant="outline" asChild>
                        <Link to={`/jobs/${approval.job_id}`}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View Job
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Approval/Rejection Dialog */}
      <Dialog open={!!selectedApproval && !!actionType} onOpenChange={() => { setSelectedApproval(null); setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Confirm Payment Receipt' : 'Reject Payment'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Confirm that you have received this payment in the platform bank account.'
                : 'Provide a reason for rejecting this payment.'}
            </DialogDescription>
          </DialogHeader>

          {selectedApproval && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference ID</span>
                  <span className="font-mono font-medium">{selectedApproval.reference_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">${(selectedApproval.amount_cents / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client</span>
                  <span>{selectedApproval.organizations?.name}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">
                  {actionType === 'approve' ? 'Transaction Notes (required)' : 'Rejection Reason (required)'}
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={actionType === 'approve' 
                    ? 'e.g., Wire transfer confirmed - Bank ref #12345'
                    : 'e.g., Payment not received after 10 business days'}
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedApproval(null); setActionType(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={!notes.trim() || approveMutation.isPending || rejectMutation.isPending}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
            >
              {actionType === 'approve' ? 'Confirm Receipt' : 'Reject Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
